import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowLeftIcon, KeyRoundIcon } from 'lucide-react';
import { Spinner } from '../../ui/Spinner';
import { useAccount } from '../../contexts/AccountContext';
import { PASSWORD_HINT } from '../../core/rules/password';
import { btnPrimary } from '../../ui/styles';
import { ease } from '../../core/format/motion';
import { PasswordInput } from './PasswordInput';
import { AuthAside } from './AuthAside';
type Props = {
  /** Terug naar "wachtwoord vergeten" om een nieuwe link aan te vragen (alleen als ze niet is ingelogd). */
  onRequestNew: () => void;
};
const FIELD_ID = 'reset-password';

/**
 * Na de link in de herstelmail (/account?herstel=<token>): één veld, en daarna is ze binnen.
 * Een verlopen of al gebruikte link krijgt een eigen staat met een weg terug, geen losse foutregel.
 */
export function PasswordResetForm({
  onRequestNew
}: Props) {
  const {
    confirmPasswordReset,
    cancelPasswordReset,
    signedIn,
    resetToken
  } = useAccount();
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [fieldError, setFieldError] = useState('');
  const [error, setError] = useState('');
  // Een lege `?herstel=` (afgebroken link) is meteen verlopen: niet eerst een wachtwoord laten kiezen.
  const [expired, setExpired] = useState(resetToken === '');
  const inputRef = useRef<HTMLInputElement>(null);
  const expiredRef = useRef<HTMLDivElement>(null);

  // Bij een verlopen link: focus naar de uitleg, zodat een schermlezer hem meteen hoort.
  useEffect(() => {
    if (expired) expiredRef.current?.focus();
  }, [expired]);
  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    setFieldError('');
    const r = await confirmPasswordReset(password);
    setBusy(false);
    if (r.ok) return;
    if (r.code === 'link_expired') {
      setExpired(true);
      return;
    }
    const onField = r.fields?.password;
    if (onField) {
      setFieldError(onField);
      inputRef.current?.focus();
    } else setError(r.error);
  };
  const requestNew = () => {
    cancelPasswordReset();
    // Ingelogd? Dan wijzigt ze het gewoon in Mijn gegevens, zonder nieuwe mail.
    if (signedIn) navigate('/account/gegevens');else onRequestNew();
  };
  return <section className="mx-auto grid max-w-content gap-12 px-5 py-12 sm:px-6 lg:grid-cols-12 lg:gap-16 lg:px-10 lg:py-20" data-id="element-6584" data-mp="3p3cf0jwcou9n">
      <motion.div key={expired ? 'expired' : 'form'} initial={reduce ? {
      opacity: 0
    } : {
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.2,
      ease
    }} className="lg:col-span-5 lg:col-start-2" data-id="element-6585" data-mp="6pwiff8zhl71">
        <button type="button" onClick={() => {
        cancelPasswordReset();
        if (signedIn) navigate('/account');
      }} className="mb-6 inline-flex items-center gap-1.5 text-[14px] text-muted transition-colors duration-150 ease-soft hover:text-ink" data-id="element-6586" data-mp="3ac3jju5roeol">
          <ArrowLeftIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-6587" data-mp="1bwmlwj6kzc3h" />
          {signedIn ? 'Terug naar Mijn omgeving' : 'Terug naar inloggen'}
        </button>

        {expired ? <div ref={expiredRef} tabIndex={-1} role="alert" className="outline-none" data-id="element-6588" data-mp="24lblgdrzn9d4">
            <h1 className="font-display text-[2.4rem] font-light leading-[1.05] text-ink sm:text-5xl" data-id="element-6589" data-mp="24jh1p2sgdnam">Deze link werkt niet meer.</h1>
            <p className="mt-4 max-w-md text-[16px] leading-relaxed text-muted" data-id="element-6590" data-mp="1unngyw4kynhr">
              Deze link is verlopen of al gebruikt. {signedIn ? 'Je bent al ingelogd: wijzig je wachtwoord in Mijn gegevens.' : 'Vraag een nieuwe aan, dan staat hij zo in je mail.'}
            </p>
            <button type="button" onClick={requestNew} className={`${btnPrimary} mt-8`} data-id="element-6591" data-mp="o71zfqb7xwpg">
              {signedIn ? 'Naar Mijn gegevens' : 'Nieuwe link aanvragen'}
            </button>
          </div> : <>
            <span className="mb-6 flex h-12 w-12 items-center justify-center rounded-full bg-ink text-shell" aria-hidden="true" data-id="element-6592" data-mp="ypqaq05bct1v">
              <KeyRoundIcon className="h-5 w-5" strokeWidth={1.5} data-id="element-6593" data-mp="uowmf5nylc8g" />
            </span>
            <h1 className="font-display text-[2.4rem] font-light leading-[1.05] text-ink sm:text-5xl" data-id="element-6594" data-mp="pqldqy9eh7rt">Kies een nieuw wachtwoord.</h1>
            <p className="mt-4 max-w-md text-[16px] leading-relaxed text-muted" data-id="element-6595" data-mp="1mdgk20vnbkmd">Daarna ben je meteen ingelogd. Op je andere apparaten log je opnieuw in.</p>

            <form onSubmit={e => void onSubmit(e)} noValidate className="mt-8 space-y-4" data-id="element-6596" data-mp="1ddtmjq61eyyl">
              <PasswordInput id={FIELD_ID} label="Nieuw wachtwoord" value={password} onChange={v => {
            setPassword(v);
            if (fieldError) setFieldError('');
          }} autoComplete="new-password" hint={PASSWORD_HINT} error={fieldError || undefined} inputRef={inputRef} data-id="element-6597" data-mp="18eqdlumxs8lc" />
              {error && <p role="alert" className="text-[14px] text-clay" data-id="element-6598" data-mp="3szq91lia72cy">
                  {error}
                </p>}
              <button type="submit" disabled={busy} className={`${btnPrimary} w-full py-3.5`} data-id="element-6599" data-mp="1mds2rfk5gl3">
                {busy && <Spinner data-id="element-6600" data-mp="5me5c8qx7lqk" />}
                {busy ? 'Bezig met opslaan…' : 'Wachtwoord opslaan'}
              </button>
            </form>
          </>}
      </motion.div>

      <AuthAside data-id="element-6601" data-mp="fl3py9a7bkai" />
    </section>;
}